import { TFunction } from 'i18next';
import { debounce, differenceWith, get, includes, isEqual, sortBy } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { Button, Grid, Pagination, PaginationProps, Popup, Table, TableProps } from 'semantic-ui-react';
import { ActionButton } from 'src/components/elements/action-handler/ActionButton';
import { DataSearchBox } from 'src/components/elements/datatable/DataSearchBox';
import { SugarCoater } from 'src/components/elements/sugar-coater/SugarCoater';
import Money from 'src/entities/basic-types/Money';
import { getCellValue } from 'src/helper/helper';

interface Props extends TableProps {
	sortable?: boolean;
	editable?: boolean;
	pageSize?: number;
	rows: DataTableRow[];
	cols: DataTableCol[];
	onCreate?: () => void;
	onCreateLabel?: string;
	onCreateExtra?: () => void;
	onCreateExtraLabel?: string;
	onDelete?: (rows: DataTableRow[] | DataTableRow, ids: number[] | number) => void;
	onPatch?: (rows: DataTableRow[] | DataTableRow, ids: number[] | number) => void;
	onPatchExtra?: (rows: DataTableRow[] | DataTableRow, ids: number[] | number) => void;
	onClone?: (rows: DataTableRow[] | DataTableRow, ids: number[] | number) => void;
	onPreview?: (rows: DataTableRow[] | DataTableRow, ids: number[] | number) => void;
	onDetails?: (rows: DataTableRow[] | DataTableRow, ids: number[] | number) => void;
	warningRow?: (row: DataTableRow, id: number) => boolean;
	negativeRow?: (row: DataTableRow, id: number) => boolean;
	onRowClick?: (rowId: number) => void;
	hideSearchBox?: boolean;
	showRowsDifference?: boolean;
	additionalToolbar?: React.ReactNode;
	deferSearch?: (searchString: string) => void;
	t: TFunction;
}

export interface DataTableRow {
	[key: string]: any;

	id: number;
	_selected?: boolean;
}

export interface DataTableCol {
	name: string;
	tooltip?: string;
	dataKey: string;
	additionalContext?: string;
	format?: (cell: any, row: any) => React.ReactNode;
	onCellClick?: (rowId: number, cell: any) => void;
	textAlign?: 'center' | 'left' | 'right';
	sortFunction?: (row: DataTableRow) => any;
}

interface State {
	activePage: number;
	totalPages: number;
	sortKey: string;
	sortDir: 'ascending' | 'descending';
	rows: DataTableRow[];
	cols: DataTableCol[];
	rowsDifference: number[];
	searchString: string;
}

export default class DataTable extends React.Component<Props, State> {
	static defaultProps: Partial<Props> = {
		editable: false,
		rows: [],
		rowsDifference: [],
		pageSize: 15,
		sortable: false,
		hideSearchBox: false,
		showRowsDifference: true,
	};

	constructor(props: Props) {
		super(props);

		const { pageSize, rows, cols, t } = props;
		const totalPages = Math.ceil(rows.length / pageSize!);

		this.state = {
			activePage: 1,
			totalPages,
			rows,
			cols: this.getCols(rows, cols, t),
			rowsDifference: [],
			sortDir: 'ascending',
			sortKey: '',
			searchString: '',
		};
	}

	UNSAFE_componentWillReceiveProps(nextProps: Props) {
		if (!isEqual(this.props.rows, nextProps.rows)) {
			// find ids of new or changed rows
			const rowsDifference =
				this.props.showRowsDifference && this.props.rows.length > 0
					? differenceWith(
							nextProps.rows,
							this.props.rows,
							(a: DataTableRow, b: DataTableRow) => a.version === b.version,
						).map(row => row.id)
					: [];

			// clear the difference after 1 second
			if (rowsDifference.length > 0) window.setTimeout(() => this.setState({ rowsDifference: [] }), 1000);

			const [filteredRows, totalPages] = this.filterRows(
				nextProps.rows,
				nextProps.pageSize!,
				this.state.searchString,
			);

			this.setState({
				rows: filteredRows,
				cols: this.getCols(nextProps.rows, nextProps.cols, nextProps.t),
				rowsDifference,
				totalPages,
				activePage: 1,
				sortKey: '',
			});

			// update columns in state because of language change
		} else if (this.props.t !== nextProps.t) {
			this.setState({ cols: this.getCols(nextProps.rows, nextProps.cols, nextProps.t) });
		}
	}

	componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
		const { pageSize = 15, rows, deferSearch } = this.props;
		const { searchString } = this.state;

		// Bail if search string did not change
		if (searchString === prevState.searchString) return;

		if (deferSearch) deferSearch(searchString);

		const [filteredRows, totalPages] = this.filterRows(rows, pageSize, searchString);
		this.setState({ rows: filteredRows, totalPages, activePage: 1 });
	}

	filterRows(rows: DataTableRow[], pageSize: number, searchString: string): [DataTableRow[], number] {
		const { t, deferSearch } = this.props;

		const filteredRows =
			searchString.length === 0 || deferSearch != null
				? rows
				: rows.filter((row: DataTableRow) => {
						return this.state.cols.some((col: DataTableCol) => {
							let currentValue = getCellValue(row, col.dataKey);

							if (currentValue == null) return false;

							if (moment.isMoment(currentValue))
								currentValue = t('format:dateTime', { dateTime: currentValue });

							currentValue = currentValue.toString();

							// Ignore case if the search string is all lower-case
							if (searchString.toLowerCase() === searchString) currentValue = currentValue.toLowerCase();

							return currentValue.includes(searchString);
						});
					});

		const totalPages = Math.ceil(filteredRows.length / pageSize!);

		return [filteredRows, totalPages];
	}

	render(): React.ReactNode {
		const { sortable, onRowClick, tableProps } = this.props;

		return (
			<div style={{ width: '100%', overflowX: 'auto' }}>
				{this.renderDatatableToolbar()}
				<Table
					color="blue"
					compact="very"
					unstackable
					singleLine
					selectable={onRowClick !== undefined}
					sortable={sortable}
					basic
					celled
					definition={this.hasAnyCallbackButtons()}
					{...tableProps}>
					{this.renderHead()}
					{this.renderBody()}
					{this.renderFoot()}
				</Table>
			</div>
		);
	}

	private renderDatatableToolbar = () => {
		const { hideSearchBox, additionalToolbar, t } = this.props;

		if (hideSearchBox && !additionalToolbar) return null;

		const searchBox = <DataSearchBox onSearchChange={debounce(this.handleSearchChange, 500, { leading: false })} />;

		if (!hideSearchBox && !additionalToolbar) return searchBox;

		if (hideSearchBox && additionalToolbar) return additionalToolbar;

		return (
			<Grid>
				<Grid.Row>
					<Grid.Column width="12">{searchBox}</Grid.Column>
					<Grid.Column textAlign="right" width="4">
						{additionalToolbar}
					</Grid.Column>
				</Grid.Row>
			</Grid>
		);
	};

	private getCols = (rows: DataTableRow[], cols: DataTableCol[], t: TFunction) => {
		if (
			rows.length > 0 &&
			rows[0].identifier &&
			cols.filter((col: DataTableCol) => col.dataKey === 'identifier' || col.dataKey.endsWith('identifier'))
				.length === 0
		)
			return [{ dataKey: 'identifier', name: t('entities:base.identifier.label') }].concat(cols);

		return cols;
	};

	private handleSearchChange = (searchString: string) => {
		this.setState({ searchString });
	};

	private renderHead() {
		const { rows } = this.props;
		const { cols } = this.state;

		const firstRow = rows[0];
		return (
			<Table.Header fullWidth>
				<Table.Row>
					{this.hasAnyCallbackButtons() ? <Table.HeaderCell /> : null}
					{cols.map(col => {
						const cell = get(firstRow, col.dataKey);

						// align money and numbers right
						const textAlign = cell instanceof Money || typeof cell === 'number' ? 'right' : 'left';
						return this.renderHeaderCell(col, col.textAlign ?? textAlign);
					})}
				</Table.Row>
			</Table.Header>
		);
	}

	private renderHeaderCell(col: DataTableCol, textAlign?: 'center' | 'left' | 'right') {
		const { sortable } = this.props;

		const { sortKey, sortDir } = this.state;

		const headerCell = (
			<Table.HeaderCell
				textAlign={textAlign ?? 'left'}
				style={col.tooltip ? { textDecoration: 'underline dotted' } : {}}
				key={`col_${col.dataKey!}`}
				sorted={sortable && sortKey === col.dataKey ? sortDir! : undefined}
				onClick={sortable ? this.handleSort(col.dataKey) : undefined}>
				{col.name}
			</Table.HeaderCell>
		);

		if (!col.tooltip) return headerCell;

		return <Popup key={col.dataKey!} trigger={headerCell} content={col.tooltip} />;
	}

	private renderBody() {
		const { onDelete, onClone, onPatch, onPatchExtra, onPreview, onDetails, warningRow, negativeRow, pageSize, t } =
			this.props;
		const { cols } = this.state;

		const sliceStart = (this.state.activePage - 1) * pageSize!;
		const sliceEnd = sliceStart + pageSize!;

		return (
			<Table.Body>
				{this.state.rows.slice(sliceStart, sliceEnd).map(row => (
					<Table.Row
						key={row.id}
						active={row._selected}
						positive={includes(this.state.rowsDifference, row.id)}
						warning={warningRow?.(row, row.id)}
						negative={negativeRow?.(row, row.id)}>
						{this.hasAnyCallbackButtons() ? (
							<Table.Cell collapsing>
								<Button.Group size="mini">
									{onDelete && (
										<ActionButton
											action="delete"
											emphasis="basic"
											variant="icon"
											onClick={this.handleDelete(row)}
										/>
									)}
									{onClone && (
										<ActionButton
											action="copy"
											emphasis="basic"
											variant="icon"
											onClick={this.handleClone(row)}
										/>
									)}
									{onPatch && (
										<ActionButton
											action="edit"
											emphasis="basic"
											variant="icon"
											onClick={this.handlePatch(row)}
										/>
									)}
									{onPatchExtra && (
										<ActionButton
											action="edit"
											emphasis="basic"
											variant="icon"
											overrideColor="green"
											overrideLabel={t('release')}
											overrideIcon="external"
											onClick={this.handlePatchExtra(row)}
										/>
									)}
									{onPreview && (
										<ActionButton
											action="search"
											emphasis="basic"
											variant="icon"
											onClick={this.handlePreview(row)}
										/>
									)}
									{onDetails && (
										<ActionButton
											action="details"
											emphasis="basic"
											variant="icon"
											onClick={this.handleDetails(row)}
										/>
									)}
								</Button.Group>
							</Table.Cell>
						) : null}
						{cols.map(col => {
							const cellValue = getCellValue(row, col.dataKey);

							// align money and numbers right
							const textAlign =
								col.textAlign != null || cellValue instanceof Money || typeof cellValue === 'number'
									? 'right'
									: 'left';

							const cellContent = col.format ? (
								col.format(cellValue, row)
							) : (
								<SugarCoater
									value={cellValue}
									context={col.dataKey}
									additionalContext={col.additionalContext}
									onClick={
										col.onCellClick ? this.handleOnCellClick(col.onCellClick, row.id) : undefined
									}
								/>
							);

							return (
								<Table.Cell
									onClick={col.onCellClick ? undefined : this.handleOnRowClick(row.id)}
									key={col.dataKey}
									textAlign={textAlign}>
									{cellContent}
								</Table.Cell>
							);
						})}
					</Table.Row>
				))}
			</Table.Body>
		);
	}

	private renderFoot() {
		const { totalPages, cols } = this.state;
		const { onCreate, onCreateLabel, onCreateExtra, onCreateExtraLabel } = this.props;

		const footerIsEmpty = totalPages <= 1 && !onCreate && !onCreateExtra;

		if (footerIsEmpty) return null;

		return (
			<Table.Footer fullWidth>
				<Table.Row>
					{this.hasAnyCallbackButtons() ? (
						<Table.HeaderCell>
							<Button.Group size="mini" fluid>
								{onCreate && (
									<ActionButton
										action="add"
										emphasis="basic"
										variant="icon"
										onClick={this.handleCreate}
										overrideLabel={onCreateLabel}
									/>
								)}
								{onCreateExtra && (
									<ActionButton
										action="add"
										emphasis="basic"
										variant="icon"
										onClick={this.handleCreateExtra}
										overrideLabel={onCreateExtraLabel}
										overrideColor="purple"
									/>
								)}
							</Button.Group>
						</Table.HeaderCell>
					) : null}
					<Table.HeaderCell colSpan={cols.length}>
						{totalPages > 1 && (
							<Pagination
								firstItem={null}
								lastItem={null}
								activePage={this.state.activePage}
								totalPages={totalPages}
								onPageChange={this.handlePagination}
							/>
						)}
					</Table.HeaderCell>
				</Table.Row>
			</Table.Footer>
		);
	}

	/* Event Handler */

	private handleOnRowClick = (rowId: number) => () => {
		const { onRowClick } = this.props;

		if (onRowClick) onRowClick(rowId);
	};

	private handleOnCellClick = (onCellClick: (rowId: number, cell: any) => void, rowId: number) => (cell: any) => {
		onCellClick(rowId, cell);
	};

	private handleSort = (key: string) => () => {
		const { rows, sortKey, sortDir } = this.state;

		if (sortKey !== key) {
			let sortedRows: DataTableRow[];
			const dataTableCol = this.state.cols.find(value => value.dataKey === key);
			const sortFunction = dataTableCol?.sortFunction;
			if (sortFunction != null) sortedRows = sortBy(this.state.rows, [sortFunction]) as DataTableRow[];
			else if (key.includes('()'))
				sortedRows = sortBy(this.state.rows, [(it: any) => getCellValue(it, key)]) as DataTableRow[];
			else if (rows[0] && rows[0][key] instanceof Money)
				sortedRows = rows.sort((rowA: DataTableRow, rowB: DataTableRow): number =>
					Money.sort(rowA[key], rowB[key]),
				);
			else sortedRows = sortBy(this.state.rows, [key]);

			this.setState({
				sortKey: key,
				sortDir: 'ascending',
				rows: sortedRows,
			});
		} else {
			this.setState({
				sortDir: sortDir === 'ascending' ? 'descending' : 'ascending',
				rows: rows.reverse(),
			});
		}
	};

	private handlePagination = (event: React.SyntheticEvent<HTMLElement>, data: PaginationProps) => {
		this.setState({ activePage: data.activePage as number });
	};

	private handleCreate = () => {
		if (this.props.onCreate != null) this.props.onCreate();
	};

	private handleCreateExtra = () => {
		if (this.props.onCreateExtra != null) this.props.onCreateExtra();
	};

	private handleDelete = (row: DataTableRow) => () => {
		if (this.props.onDelete != null) this.props.onDelete(row, row.id);
	};

	private handlePatch = (row: DataTableRow) => () => {
		if (this.props.onPatch != null) this.props.onPatch(row, row.id);
	};

	private handlePatchExtra = (row: DataTableRow) => () => {
		if (this.props.onPatchExtra != null) this.props.onPatchExtra(row, row.id);
	};

	private handleClone = (row: DataTableRow) => () => {
		if (this.props.onClone != null) this.props.onClone(row, row.id);
	};

	private handlePreview = (row: DataTableRow) => () => {
		if (this.props.onPreview != null) this.props.onPreview(row, row.id);
	};

	private handleDetails = (row: DataTableRow) => () => {
		if (this.props.onDetails != null) this.props.onDetails(row, row.id);
	};

	/* Helper */

	private hasAnyCallbackButtons(): boolean {
		const { onCreate, onCreateExtra, onDelete, onPatch, onClone, onPreview, onDetails } = this.props;
		return (
			onCreate != null ||
			onCreateExtra != null ||
			onDelete != null ||
			onPatch != null ||
			onClone != null ||
			onPreview != null ||
			onDetails != null
		);
	}
}
